'use client'

import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Image,
    Text,
    VStack,
    useToast,
    Tooltip,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { Users } from 'lucide-react'
import { useContext } from 'react'
import * as Yup from 'yup'
import React from 'react'
import dayjs from 'dayjs'

import { borderVariant12, thickBoxShadow, defaultBorder, borderVariant21 } from '@/chakraTheme'
import { Cross } from '@/components/Icons/default'
import { AuthContext } from '@/contexts/AuthContext'
import Toast from '@/common/Toast'
import { sendAlertMessage } from '@/utils/alert'

import { SESSION_CANCEL_REASON, UNTRACKED_COURSES, PULSE_TYPES } from '../_enums'
import CheckboxIcon from '../assets/checkbox.svg'
import CloseIcon from '../assets/close.svg'
import Ellipse from '../assets/ellipse753.svg'
import vectorPattern from '../assets/pattern.svg'
import TickIcon from '../assets/tick.svg'
import SearchableSelect from './SearchableDropdown' // Updated Shadcn version

const validationSchema = Yup.object().shape({
    isCancelled: Yup.boolean(),
    reason: Yup.string().required('Reason is required'),
    courseTrack: Yup.string().when('isCancelled', {
        is: false,
        then: schema => schema.required('Course/Track is required'),
        otherwise: schema => schema.notRequired(),
    }),
    agenda: Yup.string().when('isCancelled', {
        is: false,
        then: schema => schema.required('Agenda is required'),
        otherwise: schema => schema.notRequired(),
    }),
    facilitator: Yup.string().when('isCancelled', {
        is: false,
        then: schema => schema.required('Facilitator is required'),
        otherwise: schema => schema.notRequired(),
    }),
})

export default function EditSessionDetails({
    isOpen,
    onClose,
    time,
    squadNo,
    courseSlug,
    agenda,
    facilitator,
    planned_schedule_id,
    end,
    refetch,
}) {
    const { kalviumKc } = useContext(AuthContext)
    const toast = useToast()
    const [courseData, setCourseData] = React.useState({
        slugs: [],
        coursesDetails: [],
        selectedCourseDetails: null,
        learningUnits: [],
        selectedCourseType: null, // 'tracked' or 'untracked'
        predefinedAgendas: [] // Add this to store predefined agendas
    })
    const [facilitators, setFacilitators] = React.useState([])

    React.useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = await kalviumKc.getToken()

                // First, collect all predefined agendas from UNTRACKED_COURSES
                const predefinedAgendas = [
                    // Add Buffer agenda
                    // {
                    //     value: 'buffer',
                    //     label: 'Buffer',
                    //     displaySlug: 'buffer'
                    // },
                    // Add all other predefined agendas
                    ...Object.values(UNTRACKED_COURSES)?.flatMap(course =>
                        course?.agendas?.map(agenda => ({
                            value: agenda.slug,
                            label: agenda.title,
                            displaySlug: agenda.slug,
                            courseSlug: course.slug,
                        }))
                    ),
                ]

                // Fetch course slugs
                const response = await fetch(`${import.meta.env.VITE_STUDENT_API_ENDPOINT}/squad-courses/${squadNo}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                })
                if (!response.ok) throw new Error('Failed to fetch course slugs')
                const data = await response.json()
                const allSlugs = Object.values(data)?.flat()

                //Format the data for the dropdown
                const formattedCourses = allSlugs?.map(slug => ({
                    value: slug,
                    label: slug, // Default to slug, will try to fetch title
                    displaySlug: slug
                }))

                // Set initial state with predefined agendas including Buffer
                setCourseData(prev => ({
                    ...prev,
                    slugs: allSlugs,
                    coursesDetails: formattedCourses,
                    selectedCourseDetails: null,
                    predefinedAgendas,
                    learningUnits: predefinedAgendas
                }))

                // Try to fetch course titles for each slug
                try {
                    // For each course slug, try to fetch learning units to get the title
                    const coursesWithTitles = await Promise.all(
                        allSlugs?.map(async (slug) => {
                            try {
                                const luResponse = await fetch(`${import.meta.env.VITE_STUDENT_API_ENDPOINT}/learning-units/${slug}`, {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    },
                                });

                                if (luResponse.ok) {
                                    const learningUnits = await luResponse.json();
                                    return {
                                        value: slug,
                                        label: learningUnits.length > 0 ? learningUnits[0]?.title : slug,
                                        displaySlug: slug
                                    };
                                } else {
                                    console.warn(`[EditSessionDetails] Learning units fetch failed for ${slug}:`, luResponse.status, luResponse.statusText)
                                }
                                return { value: slug, label: slug, displaySlug: slug };
                            } catch (error) {
                                console.error(`[EditSessionDetails] Error fetching title for course ${slug}:`, {
                                    slug,
                                    error: error?.message,
                                    stack: error?.stack
                                });
                                return { value: slug, label: slug, displaySlug: slug };
                            }
                        })
                    );

                    setCourseData(prev => ({
                        ...prev,
                        coursesDetails: coursesWithTitles
                    }));
                } catch (error) {
                    console.error('Error fetching course titles:', error);
                }

                // Fetch facilitators
                const facilitatorResponse = await fetch(`${import.meta.env.VITE_STUDENT_API_ENDPOINT}/facilitators`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                })
                if (!facilitatorResponse.ok) throw new Error('Failed to fetch facilitators')
                const facilitatorData = await facilitatorResponse.json()

                // Format facilitator data for the new API response format
                const formattedFacilitators = facilitatorData?.map(facilitator => ({
                    value: facilitator.email,
                    label: facilitator.name,
                    displaySlug: facilitator.email,
                }))

                setFacilitators(formattedFacilitators)
            } catch (error) {
                const errorMessage = error?.message || 'Unknown error occurred'
                const errorDetails = {
                    message: errorMessage,
                    stack: error?.stack,
                    name: error?.name,
                    squadNo,
                    timestamp: new Date().toISOString()
                }
                
                console.error('[EditSessionDetails] Error fetching initial data:', errorDetails)
                
                toast({
                    duration: 5000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status="error"
                            title={<Text>Failed to fetch data: {errorMessage}</Text>}
                            {...props}
                        />
                    ),
                })
            }
        }

        if (isOpen) {
            fetchInitialData()
        }
    }, [isOpen, squadNo, kalviumKc, toast])

    // This would run after the initial data fetch
    React.useEffect(() => {
        if (isOpen && courseSlug) {
            // Call handleCourseSelect with the initial courseSlug
            handleCourseSelect(courseSlug);
        }
    }, [isOpen, courseSlug]); // Dependencies

    // Handle course selection
    const handleCourseSelect = async (courseSlug) => {
        try {
            const isUntrackedCourse = Object.keys(UNTRACKED_COURSES)?.includes(courseSlug)

            // Buffer agenda is always available
            let allAgendas = isUntrackedCourse
                ? []
                : [
                    {
                        value: 'buffer',
                        label: 'Buffer',
                        displaySlug: 'buffer',
                    },
                ]

            // Check if this is an untracked course
            if (isUntrackedCourse) {
                // Get the specific untracked course's agendas
                const courseAgendas = UNTRACKED_COURSES[courseSlug]?.agendas?.map(agenda => ({
                    value: agenda.slug,
                    label: agenda.title,
                    displaySlug: agenda.slug,
                    courseSlug: courseSlug
                }))

                // Add course-specific agendas to buffer agenda
                allAgendas = [...allAgendas, ...courseAgendas]

                setCourseData(prev => ({
                    ...prev,
                    selectedCourseDetails: { slug: courseSlug },
                    learningUnits: allAgendas
                }))
                return;
            }

            // For tracked courses, fetch learning units from the new API endpoint
            try {
                const token = await kalviumKc.getToken()

                // Fetch learning units from the new API endpoint
                const response = await fetch(`${import.meta.env.VITE_STUDENT_API_ENDPOINT}/learning-units/${courseSlug}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                })

                if (response.ok) {
                    const learningUnitsData = await response.json()

                    // Format learning units data
                    const learningUnits = learningUnitsData?.map(unit => ({
                        value: unit.slug,
                        label: unit.title,
                        displaySlug: unit.slug,
                        courseSlug: courseSlug
                    }));

                    // Add to existing agendas
                    if (learningUnits.length > 0) {
                        allAgendas = [...allAgendas, ...learningUnits];
                    }

                    // Update state with all agendas
                    setCourseData(prev => ({
                        ...prev,
                        selectedCourseDetails: { slug: courseSlug },
                        learningUnits: allAgendas || []
                    }))
                } else {
                    throw new Error('Failed to fetch learning units')
                }
            } catch (error) {
                console.error('Error fetching course learning units:', error);
                toast({
                    duration: 5000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status="error"
                            title={
                                <Text>Failed to load agenda options. Please try again.</Text>
                            }
                            {...props}
                        />
                    ),
                })

                // Fallback to just using predefined agendas
                setCourseData(prev => ({
                    ...prev,
                    selectedCourseDetails: { slug: courseSlug },
                    learningUnits: allAgendas || []
                }))
            }

        } catch (error) {
            console.error('Error in handleCourseSelect:', error);
            const isUntrackedCourse =
                Object.keys(UNTRACKED_COURSES)?.includes(courseSlug)
            if (!isUntrackedCourse) {
                toast({
                    duration: 5000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status="error"
                            title={
                                <Text>
                                    Failed to load agenda options. Please try
                                    again.
                                </Text>
                            }
                            {...props}
                        />
                    ),
                })
            }

            setCourseData(prev => ({
                ...prev,
                selectedCourseDetails: null,
                learningUnits: [
                    ...prev.predefinedAgendas,
                    isUntrackedCourse && {
                        value: 'buffer',
                        label: 'Buffer',
                        displaySlug: 'buffer',
                    },
                ],
            }))
        }
    }

    const initialValues = {
        courseTrack: courseSlug || '',
        agenda: agenda || '',
        facilitator: facilitator || '',
        reason: '',
        isCancelled: false,
        time: time
    }

    // Extract full name from email
    const getFullNameFromEmail = (email) => {
        if (!email) return '';
        const firstPart = email?.split('@')[0];
        const nameParts = firstPart.split('.');

        if (nameParts.length >= 2) {
            const firstName = nameParts?.[0]?.charAt(0)?.toUpperCase() + nameParts?.[0]?.slice(1);
            const lastName = nameParts?.[1]?.charAt(0)?.toUpperCase() + nameParts?.[1]?.slice(1);
            return `${firstName} ${lastName}`;
        }

        // If there's only one part, just capitalize it
        return firstPart?.charAt(0)?.toUpperCase() + firstPart?.slice(1);
    }

    const facilitatorFullName = getFullNameFromEmail(facilitator);

    const sendSchedulerPulse = async (pulseType, initialValues, requestBody) => {
        try {
            const alertData = {
                type: pulseType,
                metadata: {
                    initialValues,
                    requestBody
                }
            };

            await sendAlertMessage(alertData);
        } catch (error) {
            console.error('Error sending scheduler pulse:', error);
        }
    };


    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setSubmitting(true)

            const requestBody = {
                is_cancelled: values.isCancelled.toString(),
                planned_schedule_id,
                facilitator: values.facilitator,
                squad_no: squadNo.toString(),
                reason: values.reason,
                course_slug: values.courseTrack,
                agenda: values.agenda
            }

            // Check if the session has ended during form submission
            const currentTime = dayjs()
            const slotEndTime = dayjs(end)

            if (currentTime.isAfter(slotEndTime)) {
                toast({
                    duration: 5000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status="error"
                            title={
                                <Text>The session has ended. Unable to make changes.</Text>
                            }
                            {...props}
                        />
                    ),
                })
                await sendSchedulerPulse(
                    PULSE_TYPES.CHANGE_REQUEST_REJECTED,
                    initialValues,
                    "Session has already ended"
                );
                return
            }

            // Get token for authorization
            const token = await kalviumKc.getToken()

            // Send PUT request to the API
            const response = await fetch(`${import.meta.env.VITE_STUDENT_API_ENDPOINT}/scheduler/change-request`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            })

            if (!response.ok) {
                const errorMsg = await response.json()
                console.error('Error response:', errorMsg)
                throw new Error(errorMsg?.message || 'Failed to update session details')
                
            }

            toast({
                duration: 5000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status="success"
                        title={
                            <Text>Session details updated successfully.</Text>
                        }
                        {...props}
                    />
                ),
            })

            await sendSchedulerPulse(PULSE_TYPES.CHANGE_REQUEST_APPROVED, initialValues, requestBody);
            onClose()
        } catch (error) {
            console.error('Error submitting form:', error)
            toast({
                duration: 5000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status="error"
                        title={
                            <Text>Something went wrong. Please try again</Text>
                        }
                        {...props}
                    />
                ),
            })
            await sendSchedulerPulse(PULSE_TYPES.CHANGE_REQUEST_REJECTED, initialValues, error.message || "Unknown error");
        } finally {
            refetch()
            setSubmitting(false)
        }
    }

    return (
        <Drawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            size="lg"
            width={{ base: '100%', lg: '677px' }}
            variant="contained"
            borderLeft={borderVariant12}
            blockScrollOnMount={false}
        >
            <DrawerOverlay />
            <DrawerContent borderLeft={borderVariant12}>
                <Button
                    onClick={onClose}
                    position="absolute"
                    variant="unstyled"
                    border="1px solid #e3e3e3"
                    borderRadius={0}
                    h={8}
                    minW="unset"
                    w={8}
                    mr={{ base: 16 }}
                    left={{ base: '88%', md: -43 }}
                    top={{ base: '5%', md: 2 }}
                    bgColor="white"
                >
                    <Cross fontSize="2xl" />
                </Button>

                <DrawerHeader
                    pl={16}
                    pt={8}
                    pb={8}
                    pr={16}
                    bg={`url(${vectorPattern})`}
                    backgroundRepeat="repeat"
                    backgroundSize="cover"
                    borderBottom="1px solid #454545"
                    h="auto"
                >
                    <Text
                        fontWeight={700}
                        lineHeight={9}
                        color="#333"
                        fontSize={24}
                        mb="16px"
                    >
                        Edit Session Details
                    </Text>

                    <Flex alignItems="center" gap="12px">
                        <Flex alignItems="center" gap="8px">
                            <Image
                                src={CloseIcon}
                                alt="Close"
                                width={4}
                                height={4}
                            />
                            <Box px="8px" py="2px">
                                <Text
                                    fontSize="12px"
                                    color="#454545"
                                    fontFamily="Roboto"
                                >
                                    {time}
                                </Text>
                            </Box>
                        </Flex>
                        <Image src={Ellipse} alt="Ellipse" />
                        <Box px="8px" py="2px">
                            <Flex alignItems="center" gap="8px">
                                <Users size={16} />
                                <Text
                                    fontSize="12px"
                                    color="#454545"
                                    fontFamily="Roboto"
                                >
                                    Squad {squadNo}
                                </Text>
                            </Flex>
                        </Box>
                    </Flex>

                    <Box mt={4}>
                        <Flex gap={4}>
                            {[
                                { label: 'Course/Track', value: courseSlug },
                                { label: 'Agenda', value: agenda },
                                {
                                    label: 'Facilitator',
                                    value: facilitator
                                        ? facilitatorFullName
                                        : '',
                                },
                            ].map((item, index) => (
                                <Tooltip
                                    key={index}
                                    label={item.label === 'Facilitator' && facilitator ? `${facilitatorFullName} (${facilitator})` : item.value}
                                    placement="bottom-start"
                                    color={'white'}
                                    bg={'rum'}
                                    py={1}
                                    px={4}
                                    fontWeight={'normal'}
                                    border={borderVariant21}
                                    borderRadius={'none'}
                                >
                                    <Box flex="1">
                                        <Text
                                            fontSize="12px"
                                            color="#454545"
                                            fontFamily="Roboto"
                                            fontWeight="700"
                                            mb={1}
                                        >
                                            {item.label}
                                        </Text>
                                        <Text
                                            fontSize="12px"
                                            color="#454545"
                                            fontFamily="Roboto"
                                            fontWeight="400"
                                        >
                                            {item.value}
                                        </Text>
                                    </Box>
                                </Tooltip>
                            ))}
                        </Flex>
                    </Box>
                </DrawerHeader>

                <DrawerBody
                    px={16}
                    py={12}
                    pb="140px"
                    css={{
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnMount={false}
                        validateOnChange={true}
                        validateOnBlur={true}
                    >
                        {({
                            values,
                            setFieldValue,
                            isValid,
                            dirty,
                            isSubmitting,
                            touched,
                            errors
                        }) => (
                            <Form className="space-y-6">
                                <VStack spacing={6} align="stretch">
                                    <Flex alignItems="center" gap={3}>
                                        <Image
                                            src={
                                                values.isCancelled
                                                    ? TickIcon
                                                    : CheckboxIcon
                                            }
                                            alt="Checkbox"
                                            cursor="pointer"
                                            onClick={() =>
                                                setFieldValue(
                                                    'isCancelled',
                                                    !values.isCancelled
                                                )
                                            }
                                            width={6}
                                            height={6}
                                            bg={
                                                values.isCancelled
                                                    ? 'rum'
                                                    : 'transparent'
                                            }
                                        />
                                        <Text
                                            color="#191919"
                                            fontFamily="Roboto"
                                            fontSize="14px"
                                            fontStyle="normal"
                                            fontWeight="400"
                                            lineHeight="150%"
                                        >
                                            This session has been cancelled
                                        </Text>
                                    </Flex>

                                    {!values.isCancelled && (
                                        <>
                                            <SearchableSelect
                                                name="courseTrack"
                                                label="Course/Track"
                                                placeholder="Enter the course/track slug"
                                                width="w-[549px]"
                                                initialOption={{
                                                    value: initialValues.courseTrack,
                                                    label: initialValues.courseTrack,
                                                    displaySlug: initialValues.courseTrack
                                                }}
                                                options={[
                                                    ...courseData.coursesDetails,
                                                    ...Object.values(UNTRACKED_COURSES).map(course => ({
                                                        value: course.slug,
                                                        label: course.title,
                                                        displaySlug: course.slug
                                                    }))
                                                ]}
                                                onSelect={handleCourseSelect}
                                            />
                                            <SearchableSelect
                                                name="agenda"
                                                label="Agenda(LU)"
                                                placeholder="Select an agenda"
                                                options={courseData?.learningUnits}
                                                initialOption={
                                                    agenda
                                                        ? {
                                                            value: agenda,
                                                            label: agenda,
                                                            displaySlug: agenda,
                                                        }
                                                        : null
                                                }
                                                onSelect={value => {
                                                    setFieldValue('agenda', value)
                                                }}
                                                isDisabled={values.isCancelled}
                                            />
                                            <SearchableSelect
                                                name="facilitator"
                                                label="Facilitator"
                                                placeholder="Select a facilitator"
                                                width="w-[549px]"
                                                initialOption={{
                                                    value: initialValues.facilitator,
                                                    label: facilitatorFullName,
                                                    displaySlug: initialValues.facilitator,
                                                }}
                                                options={facilitators}
                                            />
                                        </>
                                    )}

                                    <SearchableSelect
                                        name="reason"
                                        label="Reason"
                                        placeholder="Select a reason"
                                        width="w-[549px]"
                                        initialOption={null}
                                        options={SESSION_CANCEL_REASON.map(reason => ({
                                            value: reason.value,
                                            displaySlug: reason.value,
                                            hideDisplaySlug: true
                                        }))}
                                    />
                                </VStack>

                                <DrawerFooter
                                    display="flex"
                                    justifyContent="flex-start"
                                    pl={16}
                                    h="106px"
                                    position="fixed"
                                    bottom={0}
                                    left="inherit"
                                    right={-2}
                                    width="677px"
                                    backgroundColor="white"
                                    zIndex={1}
                                >
                                    <Box
                                        width="549px"
                                        height="3px"
                                        position="absolute"
                                        top={0}
                                        bg="rgba(69, 69, 69, 0.20)"
                                    />
                                    <Button
                                        variant="pressable-small"
                                        type="submit"
                                        isDisabled={!dirty || !isValid || !values.reason}
                                        boxShadow={thickBoxShadow}
                                        display="inline-flex"
                                        padding="12px 24px"
                                        flexDirection="column"
                                        alignItems="flex-start"
                                        gap="10px"
                                        isLoading={isSubmitting}
                                    >
                                        Save
                                    </Button>
                                </DrawerFooter>
                            </Form>
                        )}
                    </Formik>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}
